import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { useWindowStore } from '@/stores/windowStore';
import { DESKTOP_APPS } from '@/config/desktopApps';
import type { DesktopApp } from '@/config/desktopApps';

interface StartMenuProps {
  onClose: () => void;
}

export function StartMenu({ onClose }: StartMenuProps) {
  const navigate = useNavigate();
  const { session, profile, logout } = useAuthStore();
  const { hasEnteredRabbitHole } = useThemeStore();
  const { openWindow } = useWindowStore();

  const isAuthenticated = !!session;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'moderator';

  const visibleApps = DESKTOP_APPS.filter((app) => {
    if (app.hideFromStartMenu) return false;
    if (app.authRequired && !isAuthenticated) return false;
    if (app.memberOnly && !hasEnteredRabbitHole) return false;
    if (app.normieOnly && hasEnteredRabbitHole) return false;
    if (app.hideWhenAuthenticated && isAuthenticated) return false;
    if (app.adminOnly && !isAdmin) return false;
    return true;
  });

  const resolveSize = (val: string, axis: 'w' | 'h'): number => {
    const vw = window.innerWidth;
    const vh = window.innerHeight - 40;
    const minMatch = val.match(/min\(\s*([\d.]+)(vw|vh|px)\s*,\s*([\d.]+)(vw|vh|px)\s*\)/i);
    if (minMatch) {
      const toPixels = (n: number, unit: string) =>
        unit === 'vw' ? (n / 100) * vw : unit === 'vh' ? (n / 100) * vh : n;
      return Math.min(toPixels(parseFloat(minMatch[1]), minMatch[2]), toPixels(parseFloat(minMatch[3]), minMatch[4]));
    }
    const simple = val.match(/([\d.]+)(vw|vh|px)/i);
    if (simple) {
      const n = parseFloat(simple[1]);
      if (simple[2] === 'vw') return (n / 100) * vw;
      if (simple[2] === 'vh') return (n / 100) * vh;
      return n;
    }
    return axis === 'w' ? 900 : 600;
  };

  const handleAppClick = (app: DesktopApp) => {
    openWindow(app.id, app.label, resolveSize(app.width, 'w'), resolveSize(app.height, 'h'));
    if (app.route) {
      navigate(app.route);
    }
    onClose();
  };

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  return (
    <div className="start-menu absolute bottom-10 left-0 w-64 bg-[#c0c0c0] border-2 border-t-white border-l-white border-r-[#404040] border-b-[#404040] shadow-lg z-[10001]">
      <div className="flex flex-col">
        {visibleApps.map((app) => (
          <button
            key={app.id}
            onClick={() => handleAppClick(app)}
            className="flex items-center gap-3 px-4 py-2 text-left text-black hover:bg-[#000080] hover:text-white transition-colors"
          >
            {app.icon.startsWith('/') ? (
              <img src={app.icon} alt={app.label} className="w-5 h-5 object-contain" />
            ) : (
              <span className="text-xl">{app.icon}</span>
            )}
            <span className="text-sm font-mono">{app.label}</span>
          </button>
        ))}

        {isAuthenticated && (
          <>
            <div className="h-px bg-[#808080] mx-2 my-1" />

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-left text-black hover:bg-[#000080] hover:text-white transition-colors"
            >
              <span className="text-xl">🚪</span>
              <span className="text-sm font-mono">Logout</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
